import { message } from 'antd';
import { getToken, useAuthContext } from 'contexts/AuthContext';
import { useApiMutation } from 'hooks/useApi';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import AuthService from 'services/api/AuthService';
import BaseService from 'services/api/BaseService';
import { UserUrls } from 'services/api/urls';
import * as Userpilot from 'tools/Userpilot';
import { UserOwner } from 'types/User';

export const usePasswordLoginUser = () => {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | undefined>();
  const navigate = useNavigate();
  const { login } = useAuthContext();
  const { t } = useTranslation();

  const mutation = async (email: string, password: string) => {
    setLoading(true);
    return AuthService.password_login({
      email,
      password,
    })
      .then(async (res: any) => {
        const data = await res.json();
        if (!data?.error && (res.status === 200 || res.status === 201)) {
          queryClient.clear();

          Userpilot.identify(data.user);

          login({
            user: data.user,
            token: data.jwt,
          });
          navigate('/');
        } else {
          setError(
            res.status === 401
              ? t('login:login_error')
              : data.message || t('login:login_error'),
          );
        }
      })
      .catch(() => {
        setError(t('global:internet_connexion_error'));
      })
      .finally(() => setLoading(false));
  };
  return {
    loading,
    error,
    mutation,
  };
};

export const useImpersonate = () => {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { login } = useAuthContext();
  const { t } = useTranslation();

  const mutation = async (token: string) => {
    setLoading(true);
    return AuthService.impersonate(token)
      .then(async (res: any) => {
        const data = await res.json();
        if (!data?.error && (res.status === 200 || res.status === 201)) {
          queryClient.clear();
          login({
            user: {
              ...data.user,
              impersonated: true,
            },
            token: data.jwt,
          });
          navigate('/');
        } else {
          navigate('/connexion');
          message.error(data.message || t('login:token_error'));
        }
      })
      .catch(() => {
        navigate('/connexion');
        message.error(t('global:internet_connexion_error'));
      })
      .finally(() => setLoading(false));
  };
  return {
    loading,
    mutation,
  };
};

class ExpiredLinkError extends Error {}

export const usePasswordMutation = () => {
  const { t } = useTranslation();

  const initMutation = useMutation<unknown, Error, string>({
    mutationFn: async (email) => {
      const res: any = await AuthService.reset_password_init(email);
      const data = await res.json();
      if (!data?.error && (res.status === 200 || res.status === 201)) {
        return data;
      }
      throw new Error(
        data.message ?? data.error?.message ?? t('reset_password:error'),
      );
    },
  });

  const resetMutation = useMutation<
    unknown,
    Error,
    {
      newPassword: string;
      email: string;
      token: string;
    }
  >({
    mutationFn: async ({
      newPassword,
      email,
      token,
    }) => {
      const res: any = await AuthService.reset_password({
        newPassword,
        email,
        token,
      });
      const data = await res.json();
      if (!data?.error && (res.status === 200 || res.status === 201)) {
        return data;
      }
      throw new Error(
        data.message ?? data.error?.message ?? t('reset_password:error'),
      );
    },
  });

  const createMutation = useMutation<
    unknown,
    Error,
    {
      newPassword: string;
      token: string;
      email: string;
    }
  >({
    mutationFn: async ({
      newPassword,
      token,
      email,
    }) => {
      const res: any = await AuthService.create_password({
        newPassword,
        token,
        email,
      });
      const data = await res.json();
      if (!data?.error && (res.status === 200 || res.status === 201)) {
        return data;
      }
      if (res.status === 410) {
        throw new ExpiredLinkError();
      }
      throw new Error(
        data.message ?? data.error?.message ?? t('first_login:error'),
      );
    },
  });

  const activeError =
    (initMutation.error as Error | null) ??
    (resetMutation.error as Error | null) ??
    (createMutation.error as Error | null);

  return {
    loading:
      initMutation.isPending ||
      resetMutation.isPending ||
      createMutation.isPending,
    error:
      activeError instanceof ExpiredLinkError
        ? undefined
        : activeError?.message,
    success:
      initMutation.isSuccess ||
      resetMutation.isSuccess ||
      createMutation.isSuccess,
    isExpired: createMutation.error instanceof ExpiredLinkError,
    init: (email: string) => initMutation.mutate(email),
    reset: (newPassword: string, email: string, token: string) =>
      resetMutation.mutate({ newPassword, email, token }),
    create: (newPassword: string, token: string, email: string) =>
      createMutation.mutate({ newPassword, token, email }),
  };
};

export function useResendEmail() {
  return useApiMutation(UserUrls.RESEND_ACCOUNT_EMAIL);
}

export const useUserOwner = () => {
  const { t } = useTranslation();

  return useQuery<UserOwner, Error>({
    queryKey: [UserUrls.GET_ACCOUNT_OWNER_INFO],
    queryFn: async () => {
      let data: any;
      if (!getToken()) {
        return {} as UserOwner;
      }
      try {
        const response = await BaseService.getRequest(
          UserUrls.GET_ACCOUNT_OWNER_INFO,
          true,
        );
        data = await response.json();
        if ([200, 201].includes(response.status)) {
          return data;
        }
      } catch (e: any) {
        console.warn('Get account info error', e);
        throw new Error(t('global:internet_connexion_error'));
      }
      throw new Error(data?.message || t('global:internet_connexion_error'));
    },
    staleTime: Infinity,
  });
};
